import { useEffect, useState, useRef } from "react";
import { HashRouter, Navigate, Outlet, Route, Routes, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Rail } from "./views/parts/Rail";
import { Toasts } from "./views/parts/Toasts";
import { Home } from "./views/Home";
import { Wizard } from "./views/Wizard";
import { Workspace } from "./views/Workspace";
import { useSettings } from "./state/settings";
import { useWorkspaces } from "./state/workspaces";
import * as api from "./api/client";
import { toast } from "./notify";
import { texte } from "./texte";

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1 } },
});

function Shell() {
  return (
    <div className="shell">
      <Rail />
      <Outlet />
    </div>
  );
}

/**
 * Öffnet der Client einen noch unbekannten Token/Workspace aus der URL, registriert er ihn
 * automatisch (client.md §5.2) — Zugang kommt ausschließlich über den Link, kein Formular.
 */
function WorkspaceRoute() {
  const { token = "", workspace = "" } = useParams<{ token: string; workspace: string }>();
  const [searchParams] = useSearchParams();
  const apiUrl = useSettings((s) => s.apiUrl);
  const byName = useWorkspaces((s) => s.byName);
  const upsert = useWorkspaces((s) => s.upsert);
  const navigate = useNavigate();
  const [checking, setChecking] = useState(!byName[workspace]);
  const loginAttempted = useRef("");

  useEffect(() => {
    let cancelled = false;
    if (byName[workspace]) {
      setChecking(false);
      return;
    }
    const key = `${workspace}:${token}`;
    if (loginAttempted.current === key) return;
    loginAttempted.current = key;
    setChecking(true);
    (async () => {
      const res = await api.login(apiUrl, token).catch(() => ({ code: -1 }));
      if (cancelled) return;
      if (res.code !== 0) {
        toast(texte.dialogs.join.unknownToken, texte.errors.linkInvalid);
        navigate("/", { replace: true });
        return;
      }
      const matchingAdmin = Object.values(byName).find((w) => w.token === token && w.role === "admin");
      upsert({
        workspace,
        token,
        role: matchingAdmin ? "admin" : "student",
        view: matchingAdmin ? "admin" : "simple",
        origin: matchingAdmin ? "created" : "redeemed",
        addedAt: Date.now(),
        notifiedQids: [],
        expertEnabled: searchParams.get("expert") === "1",
      });
      setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, workspace]);

  if (checking) return null;
  return <Workspace />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <HashRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          <Route element={<Shell />}>
            <Route path="/" element={<Home />} />
            <Route path="/neu" element={<Wizard />} />
            <Route path="/ws/:token/:workspace" element={<WorkspaceRoute />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </HashRouter>
      <Toasts />
    </QueryClientProvider>
  );
}
